import AddIcon from '@mui/icons-material/Add';
import { Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import {
  selectProducts,
  selectIsLoading as selectProductsLoading,
  selectIsFailed as selectProductsFailed,
  selectIsLoaded as selectProductsLoaded,
  setupProducts,
} from '@/entities/products';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import {
  Button,
  DataRecordCard,
  FatalError,
  Loader,
  Panel,
  WebLinksIcon,
} from '@/shared/ui';
import {
  selectIsFailed as selectWebLinksFailed,
  selectWebLinks,
  setupWebLinks,
  selectIsLoading as selectWebLinksLoading,
  deleteWebLink,
} from '../../model/slice';
import { WebLinkType } from '../../model/types';
import { AddWebLinkRow } from '../AddWebLinkRow/AddWebLinkRow';

const LEGACY_WEB_LINKS_LABELS_MAP: Record<WebLinkType, string> = {
  [WebLinkType.webApplication]: 'Web Application',
  [WebLinkType.webFunnel]: 'Web Funnel',
  [WebLinkType.website]: 'Website',
  [WebLinkType.web2App]: 'Web2App',
};

export function WebLinksTable() {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const webLinks = useAppSelector(selectWebLinks);
  const isWebLinksError = useAppSelector(selectWebLinksFailed);
  const isWebLinksLoading = useAppSelector(selectWebLinksLoading);
  const products = useAppSelector(selectProducts);
  const isProductsLoading = useAppSelector(selectProductsLoading);
  const isProductsLoaded = useAppSelector(selectProductsLoaded);
  const isProductsError = useAppSelector(selectProductsFailed);
  const productNamesMap = useMemo(() => {
    return products.reduce<Record<string, string>>((acc, product) => {
      acc[product.id] = product.name;
      return acc;
    }, {});
  }, [products]);
  const [createEditorOpened, setCreateEditorOpened] = useState(false);
  const showEmptyState =
    !createEditorOpened && webLinks.length === 0 && !isWebLinksLoading;

  useEffect(() => {
    dispatch(setupProducts());
    dispatch(setupWebLinks());
  }, [dispatch]);

  if (isWebLinksError || isProductsError) {
    return <FatalError />;
  }

  if (isProductsLoaded && products.length === 0) {
    return (
      <Panel sx={{ p: 2, flexGrow: 1 }}>
        <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
          Products for your organization are not set up yet. Please contact{' '}
          <a href="mailto:support@campaignswell.com?subject=Setup products for organization">
            support
          </a>
        </Typography>
      </Panel>
    );
  }

  const handleOpenCreateEditor = () => {
    setCreateEditorOpened(true);
  };
  const handleCloseCreateEditor = () => {
    setCreateEditorOpened(false);
  };

  const handleDelete = (id: string) => () => {
    return dispatch(deleteWebLink(id))
      .unwrap()
      .then(() => {
        enqueueSnackbar('Web link successfully deleted.', {
          variant: 'success',
          autoHideDuration: 2000,
        });
      })
      .catch(() => {
        enqueueSnackbar('Unable to delete web link. Please, try again.', {
          variant: 'error',
          autoHideDuration: 4000,
        });
      });
  };

  return (
    <Panel
      sx={{
        flexGrow: 1,
        p: 2,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
      }}
    >
      <Button
        sx={{
          height: 40,
          borderRadius: 2,
          fontSize: '0.75rem',
          lineHeight: '1.125rem',
        }}
        variant="outlined"
        startIcon={<AddIcon />}
        disabled={createEditorOpened}
        onClick={handleOpenCreateEditor}
      >
        Add Link
      </Button>
      {createEditorOpened && (
        <AddWebLinkRow onClose={handleCloseCreateEditor} />
      )}
      <Box sx={{ flexGrow: 1, position: 'relative', alignSelf: 'normal' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'auto',

            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {webLinks.map((webLink) => {
            return (
              <DataRecordCard
                key={webLink.id}
                sx={{ width: '100%' }}
                slotProps={{
                  fields: { sx: { justifyContent: 'flex-start' } },
                }}
                recordNoun="link"
                title={webLink.url}
                fields={[
                  {
                    label: 'Product',
                    value: productNamesMap[webLink.productId],
                  },
                  {
                    label: 'Type',
                    value: LEGACY_WEB_LINKS_LABELS_MAP[webLink.type],
                  },
                ]}
                onDelete={handleDelete(webLink.id)}
              />
            );
          })}
        </Box>
      </Box>
      {showEmptyState && (
        <Box
          sx={{
            flexGrow: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <WebLinksIcon sx={{ width: 100, height: 100, color: '#989CA9' }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography sx={{ fontWeight: 500, fontSize: '1.125rem' }}>
              Add Your First Link
            </Typography>
            <Typography color="secondary" sx={{ fontSize: '0.875rem' }}>
              There's nothing here yet. Get started by adding a link
            </Typography>
          </Box>
        </Box>
      )}
      <Loader active={isProductsLoading || isWebLinksLoading} />
    </Panel>
  );
}
